import { useState, useEffect } from 'react';
import { Calendar, Phone, DollarSign, RefreshCw, ArrowLeft, Search } from 'lucide-react';
import API_BASE_URL from '../../config';
import Extendplan from './Extendplan';
import RecordPaymentView from './RecordPaymentView';
import axios from 'axios';

const RenewalsDueTab = ({ onBack, initialSegment = '1-3' }) => {
  const [segment, setSegment] = useState(initialSegment); // '1-3', '4-7', '8-15'
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [extendMember, setExtendMember] = useState(null);
  const [showAddPay, setShowAddPay] = useState(false);
  const [selectedPayMember, setSelectedPayMember] = useState(null);

  const gymCode = JSON.parse(localStorage.getItem('fitscan_user') || '{}').gymcode;

  const fetchRenewals = async () => {
    if (!gymCode) return;
    setLoading(true);
    setError('');

    let minDays = 0;
    let maxDays = 3;

    if (segment === '4-7') {
      minDays = 4;
      maxDays = 7;
    } else if (segment === '8-15') {
      minDays = 8;
      maxDays = 15;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/gym/renewals-due`, {
        gymCode,
        minDays,
        maxDays
      }, {
        headers: {
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
        }
      });
      setMembers(response.data);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch renewals. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRenewals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segment]);

  const filteredMembers = members.filter(m => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      m.name?.toLowerCase().includes(query) ||
      m.phone?.toLowerCase().includes(query) ||
      m.plan?.toLowerCase().includes(query)
    );
  });

  const getUrgencyStyles = () => {
    if (segment === '1-3') return { text: 'text-red-400', bg: 'bg-red-500/10 border-red-500/30' };
    if (segment === '4-7') return { text: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/30' };
    return { text: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/30' };
  };

  const urgency = getUrgencyStyles();

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 bg-[#222] border border-[#2b2b2b] hover:bg-[#2b2b2b] rounded-lg text-gray-400 hover:text-white transition-all"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Calendar size={24} className="text-lime-400" />
              Renewals Due
            </h1>
            <p className="text-gray-400 text-sm">Members with memberships expiring soon</p>
          </div>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-64">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search renewals..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg focus:outline-none focus:ring-2 focus:ring-lime-400 text-sm"
            />
          </div>
          <button
            onClick={fetchRenewals}
            className="p-2.5 bg-[#222] border border-[#2b2b2b] hover:bg-[#2b2b2b] rounded-lg text-gray-400 hover:text-white"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Expiry filter chips */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 md:-mx-0 md:px-0 scrollbar-none">
        {[
          { id: '1-3', label: '1 - 3 Days' },
          { id: '4-7', label: '4 - 7 Days' },
          { id: '8-15', label: '8 - 15 Days' }
        ].map((item) => (
          <button
            key={item.id}
            onClick={() => setSegment(item.id)}
            className={`px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${segment === item.id
                ? segment === '1-3'
                  ? 'bg-red-500 text-white shadow-lg shadow-red-500/20'
                  : segment === '4-7'
                    ? 'bg-amber-500 text-zinc-950 shadow-lg shadow-amber-500/20'
                    : 'bg-emerald-500 text-zinc-950 shadow-lg shadow-emerald-500/20'
                : 'bg-[#222] text-gray-400 hover:bg-[#2b2b2b]'
              }`}
          >
            {item.label}
          </button>
        ))}
      </div>      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading renewals...</div>
      ) : error ? (
        <div className="text-center text-red-500 py-6">{error}</div>
      ) : (
        <>
          {/* Mobile view list */}
          <div className="block md:hidden space-y-4">
            {filteredMembers.length === 0 ? (
              <div className="p-16 text-center text-gray-400 bg-[#181818] border border-[#2b2b2b] rounded-2xl">
                <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-500" />
                <div>No renewals due found for this period.</div>
              </div>
            ) : (
              filteredMembers.map((member, idx) => (
                <div key={idx} className="bg-[#181818] border border-[#2b2b2b] rounded-2xl p-4 space-y-4">
                  <div className="flex justify-between items-start">
                    <div className="space-y-1">
                      <h3 className="font-bold text-white text-base leading-snug">{member.name}</h3>
                      <div className="flex items-center gap-1 text-gray-400 text-xs">
                        <Phone size={12} />
                        <span>+91 {member.phone?.slice(-10)}</span>
                      </div>
                    </div>
                    <span className={`px-2.5 py-1 rounded-xl text-xs font-bold border ${urgency.text} ${urgency.bg}`}>
                      {new Date(member.expiryDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                  </div>

                  <div className="bg-[#1a1a1a] rounded-xl p-3 flex justify-between items-center text-xs">
                    <div>
                      <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">Plan</div>
                      <span className="bg-lime-500/10 text-lime-400 px-2 py-0.5 rounded font-medium border border-lime-500/20">
                        {member.plan || 'Basic'}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">Due Amount</div>
                      <span className="font-bold text-red-400 text-sm">
                        ₹{member.dueAmount?.toLocaleString() || '0'}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setExtendMember({
                          username: member.phone,
                          ExpiryDate: member.expiryDate?.split('T')[0],
                          Firstname: member.name?.split(' ')[0],
                          Lastname: member.name?.split(' ')[1] || '',
                          PlanName: member.plan
                        });
                      }}
                      className="flex-1 bg-lime-400 hover:bg-lime-300 text-zinc-950 text-xs font-bold py-2.5 rounded-xl transition-all text-center cursor-pointer"
                    >
                      Renew Membership
                    </button>
                    {member.dueAmount > 0 && (
                      <button
                        onClick={() => {
                          setSelectedPayMember(member);
                          setShowAddPay(true);
                        }}
                        className="flex-1 bg-[#222] hover:bg-[#2b2b2b] text-white border border-[#2b2b2b] text-xs font-semibold py-2.5 rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                      >
                        <DollarSign size={14} />
                        Add Pay
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Desktop view table */}
          <div className="hidden md:block bg-[#181818] border border-[#2b2b2b] rounded-xl overflow-hidden shadow-xl">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[700px]">
                <thead>
                  <tr className="bg-[#222] border-b border-[#2b2b2b] text-gray-300 font-semibold text-xs uppercase tracking-wider">
                    <th className="px-6 py-4">Name</th>
                    <th className="px-6 py-4">Phone</th>
                    <th className="px-6 py-4">Expiry Date</th>
                    <th className="px-6 py-4">Current Plan</th>
                    <th className="px-6 py-4">Due Amount</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#2b2b2b] text-sm text-gray-200">
                  {filteredMembers.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-500 bg-[#1a1a1a]">
                        No renewals due found for this period.
                      </td>
                    </tr>
                  ) : (
                    filteredMembers.map((member, idx) => (
                      <tr key={idx} className="hover:bg-[#222]/50 transition-colors">
                        <td className="px-6 py-4 font-medium text-white">{member.name}</td>
                        <td className="px-6 py-4 text-gray-400">
                          <span className="flex items-center gap-1.5">
                            <Phone size={14} className="text-gray-500" />
                            +91 {member.phone?.slice(-10)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${urgency.text} ${urgency.bg}`}>
                            {new Date(member.expiryDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-0.5 rounded bg-lime-500/10 text-lime-400 text-xs font-medium border border-lime-500/20">
                            {member.plan || 'Basic'}
                          </span>
                        </td>
                        <td className="px-6 py-4 font-semibold text-white">
                          ₹{member.dueAmount?.toLocaleString() || '0'}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => {
                                setExtendMember({
                                  username: member.phone,
                                  ExpiryDate: member.expiryDate?.split('T')[0],
                                  Firstname: member.name?.split(' ')[0],
                                  Lastname: member.name?.split(' ')[1] || '',
                                  PlanName: member.plan
                                });
                              }}
                              className="bg-lime-400 hover:bg-lime-300 text-zinc-950 text-xs font-bold px-3 py-1.5 rounded-lg transition-all cursor-pointer"
                            >
                              Renew
                            </button>
                            {member.dueAmount > 0 && (
                              <button
                                onClick={() => {
                                  setSelectedPayMember(member);
                                  setShowAddPay(true);
                                }}
                                className="bg-[#222] hover:bg-[#2b2b2b] text-white border border-[#333] text-xs font-medium px-3 py-1.5 rounded-lg transition-all flex items-center gap-1 cursor-pointer"
                              >
                                <DollarSign size={12} />
                                Add Pay
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      {/* Extend plan modal */}
      {extendMember && (
        <Extendplan
          member={extendMember}
          onClose={() => setExtendMember(null)}
          onAddMember={() => {
            fetchRenewals();
            setExtendMember(null);
          }}
        />
      )}

      {/* Add payment modal */}
      {showAddPay && (
        <RecordPaymentView
          initialMemberId={selectedPayMember?.id || ''}
          onClose={() => {
            setShowAddPay(false);
            setSelectedPayMember(null);
          }}
          onRecordPayment={() => {
            fetchRenewals();
            setShowAddPay(false);
            setSelectedPayMember(null);
          }}
        />
      )}
    </div>
  );
};

export default RenewalsDueTab;
